import { useState } from 'react';
import { Loader, X } from 'lucide-react';
import type { UsdaPortionCandidate } from '../../../types/unitConversion';
import { applyUsdaBulkMatchReview } from '../../../utils/unitConversionApi';

interface Props {
  ingredientId: string;
  ingredientName: string;
  nameEn?: string | null;
  candidates: UsdaPortionCandidate[];
  onClose: () => void;
  onApplied: () => void;
}

export function UsdaSuggestModal({
  ingredientId,
  ingredientName,
  nameEn,
  candidates,
  onClose,
  onApplied,
}: Props) {
  const [selected, setSelected] = useState(candidates[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await applyUsdaBulkMatchReview(ingredientId, selected);
      onApplied();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-display font-bold text-menioo-navy">
              Suggestion USDA — poids pièce
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {ingredientName}
              {nameEn ? ` → ${nameEn}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          {candidates.length === 0 ? (
            <p className="text-sm text-gray-600">
              Aucun candidat USDA. Vous pourrez renseigner le poids manuellement plus tard.
            </p>
          ) : (
            <ul className="space-y-2">
              {candidates.map((c) => (
                <li key={c.id}>
                  <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 hover:border-menioo-green cursor-pointer">
                    <input
                      type="radio"
                      name="usda"
                      checked={selected === c.id}
                      onChange={() => setSelected(c.id)}
                      className="mt-1 text-menioo-green focus:ring-menioo-green"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-menioo-navy">{c.descriptionEn}</span>
                      <span className="text-gray-500"> — {c.modifier}</span>
                      <br />
                      <span className="text-menioo-green font-medium">
                        {Math.round(c.unitWeight_g * 10) / 10} g
                      </span>
                      <span className="text-gray-400 ml-2">score {c.score}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Plus tard
          </button>
          <button
            type="button"
            disabled={busy || !selected || candidates.length === 0}
            onClick={apply}
            className="inline-flex items-center px-4 py-2 text-sm rounded-md text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy && <Loader className="h-4 w-4 mr-2 animate-spin" />}
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}
